/**
 * Sample data for the supporter-import previews. Stands in for the props the
 * real pages will get from Groups::ImportsController, ImportsController and
 * the `import` GraphQL query once P1.1 records runs.
 *
 * The shape follows the M0 run model: one `Import` per submitted file, with
 * per-row outcomes whose counts sum to the submitted row count.
 */

export type ImportType = 'supporters' | 'event_rsvps'

export type RunStatus = 'queued' | 'working' | 'completed' | 'completed_with_failures' | 'failed'

export type OutcomeKind = 'failed' | 'skipped'

export type RowOutcome = {
  row: number
  /** The full name on the row. */
  identifier: string
  kind: OutcomeKind
  /** Plain language, from the stable reason key P1.2 records. */
  reason: string
}

export type RunCounts = { created: number; updated: number; failed: number; skipped: number }

export type ImportRun = {
  id: number
  type: ImportType
  /** Every row goes into this one group. It is stored on the run, never a column in the file. */
  group: string
  status: RunStatus
  importer: string
  submittedAt: string
  fileName: string
  submittedRowCount: number
  processedRowCount: number
  counts: RunCounts
  overwrite: boolean
  /** Only meaningful for event RSVP runs. */
  markAsAttended: boolean
  event: string | null
  outcomes: RowOutcome[]
}

/** The most rows one import accepts. CSVBox `max_rows` enforces it on upload. */
export const MAX_ROWS = 10000

/** How long the uploaded file is kept before P5.4 purges it. */
export const FILE_RETENTION_DAYS = 90

export const GROUP = { id: 12, name: 'Warringah Volunteers', supporterCount: 686 }

export const IMPORTER = 'Priya Raman'

/** Who marked a run as reviewed, and when, for the optional review extension. */
export const REVIEWER = 'Tom Alder'
export const REVIEWED_ON = '4 Nov 2026'

export type ImportEvent = { id: string; name: string; date: string; past: boolean; group: string }

/** Events across the instance, newest first. A captain sees only their group's; an admin sees them all. */
export const EVENTS: ImportEvent[] = [
  { id: 'e9', name: 'Election day booths', date: 'Sat 21 Nov 2026', past: false, group: GROUP.name },
  { id: 'e8', name: 'Pre-poll roster briefing', date: 'Tue 10 Nov 2026', past: false, group: GROUP.name },
  { id: 'b2', name: 'Chatswood street stall', date: 'Sun 8 Nov 2026', past: false, group: 'Bradfield Volunteers' },
  { id: 'e7', name: 'Dee Why doorknock', date: 'Sat 7 Nov 2026', past: false, group: GROUP.name },
  { id: 'e6', name: 'Manly Corso street stall', date: 'Sat 24 Oct 2026', past: true, group: 'Mackellar Volunteers' },
  { id: 'e5', name: 'Volunteer training night', date: 'Thu 15 Oct 2026', past: true, group: GROUP.name },
  { id: 'e4', name: 'Phone bank, week 3', date: 'Wed 7 Oct 2026', past: true, group: GROUP.name },
  { id: 'e3', name: 'Campaign launch', date: 'Sun 20 Sep 2026', past: true, group: 'Mackellar Volunteers' },
]

export type ColumnSpec = {
  name: string
  label: string
  required: boolean
  /** Always in the template: email, and first and last name since every row needs one of them. */
  alwaysIncluded?: boolean
  /** Columns that are only ever included or left out together, behind one checkbox. */
  bundle?: 'address'
  hint: string
  example: string
}

/**
 * The supporter sheet, without the group column (the group is fixed by the
 * page) and without the four contact-preference columns, which the CRM owns on
 * NationBuilder (P1.6). This is the list P2.1 will make the single source for.
 */
export const SUPPORTER_COLUMNS: ColumnSpec[] = [
  {
    name: 'first_name',
    alwaysIncluded: true,
    label: 'First name',
    required: false,
    hint: 'First or last name is needed on every row.',
    example: 'Maria',
  },
  {
    name: 'last_name',
    alwaysIncluded: true,
    label: 'Last name',
    required: false,
    hint: 'First or last name is needed on every row.',
    example: 'Rossi',
  },
  {
    name: 'email',
    alwaysIncluded: true,
    label: 'Email',
    required: true,
    hint: 'How we find the person. Rows without one fail.',
    example: 'maria@example.org',
  },
  {
    name: 'mobile',
    label: 'Mobile',
    required: false,
    hint: 'Digits only, with or without the country code.',
    example: '0412 000 111',
  },
  { name: 'phone', label: 'Phone', required: false, hint: 'A landline or second number.', example: '02 9000 0000' },
  {
    name: 'address',
    label: 'Street address',
    bundle: 'address',
    required: false,
    hint: 'Street number and name. Unit on the same line is fine.',
    example: '12 Pittwater Rd',
  },
  { name: 'suburb', label: 'Suburb', bundle: 'address', required: false, hint: '', example: 'Dee Why' },
  {
    name: 'state',
    label: 'State',
    bundle: 'address',
    required: false,
    hint: 'Short form or in full. NSW and New South Wales both work.',
    example: 'NSW',
  },
  { name: 'postcode', label: 'Postcode', bundle: 'address', required: false, hint: '', example: '2099' },
  {
    name: 'country',
    label: 'Country',
    bundle: 'address',
    required: false,
    hint: 'Leave blank for Australia.',
    example: 'Australia',
  },
  {
    name: 'tags',
    label: 'Tags',
    required: false,
    hint: 'Comma-separated.',
    example: 'doorknocker, north',
  },
  { name: 'birthdate', label: 'Birthdate', required: false, hint: 'YYYY-MM-DD.', example: '1988-03-14' },
  {
    name: 'note',
    label: 'Note',
    required: false,
    hint: 'Added to the person as a note from you, with the date.',
    example: 'Met at the Corso stall',
  },
]

export const COLUMN_BUNDLES = {
  address: {
    label: 'Address',
    hint: 'Street address, suburb, state, postcode and country. A partial address replaces the whole stored one.',
  },
} as const

export const RSVP_ONLY_COLUMNS: ColumnSpec[] = [
  { name: 'rsvp_status', label: 'RSVP', required: false, hint: 'yes or no. Blank counts as yes.', example: 'yes' },
  {
    name: 'guests',
    label: 'Guests',
    required: false,
    hint: 'Extra people they are bringing, as a whole number.',
    example: '2',
  },
]

const SETTLED_OUTCOMES: RowOutcome[] = [
  { row: 7, identifier: 'Jo Nguyen', kind: 'failed', reason: 'The email address is not valid.' },
  { row: 19, identifier: 'Sam Kaur', kind: 'failed', reason: 'There is no email address on this row.' },
  {
    row: 23,
    identifier: 'Ali Tran',
    kind: 'failed',
    reason: 'The birthdate 31/02/1990 is not a date we recognise.',
  },
  { row: 31, identifier: 'Maria Costa', kind: 'skipped', reason: 'Same email as row 4. The first row was used.' },
]

/** A finished group-scoped run. The counts sum to the submitted rows: 31 + 13 + 3 + 1 = 48. */
export const SETTLED_RUN: ImportRun = {
  id: 42,
  type: 'supporters',
  group: GROUP.name,
  status: 'completed_with_failures',
  importer: IMPORTER,
  submittedAt: '2 Nov 2026, 9:14am',
  fileName: 'warringah-volunteers-supporters.csv',
  submittedRowCount: 48,
  processedRowCount: 48,
  counts: { created: 31, updated: 13, failed: 3, skipped: 1 },
  overwrite: false,
  markAsAttended: false,
  event: null,
  outcomes: SETTLED_OUTCOMES,
}

/** The same run while the job is still processing rows. */
export const WORKING_RUN: ImportRun = {
  ...SETTLED_RUN,
  status: 'working',
  submittedRowCount: 1240,
  processedRowCount: 412,
  counts: { created: 288, updated: 120, failed: 3, skipped: 1 },
  outcomes: SETTLED_OUTCOMES,
}

/** A clean run with nothing to review. */
export const CLEAN_RUN: ImportRun = {
  ...SETTLED_RUN,
  id: 41,
  status: 'completed',
  submittedRowCount: 20,
  processedRowCount: 20,
  counts: { created: 18, updated: 2, failed: 0, skipped: 0 },
  outcomes: [],
}

/** The admin history, newest first, mixing both run types and every status. */
export const HISTORY_RUNS: ImportRun[] = [
  {
    id: 44,
    type: 'supporters',
    group: 'North Sydney Volunteers',
    status: 'working',
    importer: 'Tom Alder',
    submittedAt: '2 Nov 2026, 11:02am',
    fileName: 'north-sydney-october.csv',
    submittedRowCount: 1240,
    processedRowCount: 412,
    counts: { created: 288, updated: 120, failed: 3, skipped: 1 },
    overwrite: true,
    markAsAttended: false,
    event: null,
    outcomes: SETTLED_OUTCOMES,
  },
  {
    id: 43,
    type: 'event_rsvps',
    group: 'Mackellar Volunteers',
    status: 'queued',
    importer: 'Chiara Conti',
    submittedAt: '2 Nov 2026, 10:58am',
    fileName: 'stall-signins.csv',
    submittedRowCount: 36,
    processedRowCount: 0,
    counts: { created: 0, updated: 0, failed: 0, skipped: 0 },
    overwrite: false,
    markAsAttended: true,
    event: 'Manly Corso street stall',
    outcomes: [],
  },
  SETTLED_RUN,
  CLEAN_RUN,
  {
    id: 40,
    type: 'event_rsvps',
    group: GROUP.name,
    status: 'completed',
    importer: IMPORTER,
    submittedAt: '26 Oct 2026, 6:40pm',
    fileName: 'training-night-attendance.csv',
    submittedRowCount: 27,
    processedRowCount: 27,
    counts: { created: 4, updated: 23, failed: 0, skipped: 0 },
    overwrite: false,
    markAsAttended: true,
    event: 'Volunteer training night',
    outcomes: [],
  },
  {
    id: 39,
    type: 'supporters',
    group: 'Bradfield Volunteers',
    status: 'failed',
    importer: 'Tom Alder',
    submittedAt: '19 Oct 2026, 2:15pm',
    fileName: 'bradfield-signups.csv',
    submittedRowCount: 310,
    processedRowCount: 0,
    counts: { created: 0, updated: 0, failed: 0, skipped: 0 },
    overwrite: false,
    markAsAttended: false,
    event: null,
    outcomes: [],
  },
  {
    id: 38,
    type: 'supporters',
    group: 'Bradfield Volunteers',
    status: 'completed_with_failures',
    importer: 'Tom Alder',
    submittedAt: '3 Oct 2026, 8:05am',
    fileName: 'petition-signers-sept.csv',
    submittedRowCount: 2000,
    processedRowCount: 2000,
    counts: { created: 1412, updated: 501, failed: 61, skipped: 26 },
    overwrite: false,
    markAsAttended: false,
    event: null,
    outcomes: [],
  },
  {
    id: 37,
    type: 'event_rsvps',
    group: 'Mackellar Volunteers',
    status: 'completed',
    importer: 'Chiara Conti',
    submittedAt: '21 Sep 2026, 7:30pm',
    fileName: 'launch-rsvps.csv',
    submittedRowCount: 142,
    processedRowCount: 142,
    counts: { created: 58, updated: 84, failed: 0, skipped: 0 },
    overwrite: false,
    markAsAttended: false,
    event: 'Campaign launch',
    outcomes: [],
  },
]

export const ALL_GROUPS = [
  'Bradfield Volunteers',
  'Mackellar Volunteers',
  'North Sydney Volunteers',
  'Warringah Volunteers',
  'Wentworth Volunteers',
]

export const TYPE_LABELS: Record<ImportType, string> = {
  supporters: 'Supporters',
  event_rsvps: 'Event RSVPs',
}

export const STATUS_LABELS: Record<RunStatus, string> = {
  queued: 'Queued',
  working: 'Working',
  completed: 'Completed',
  completed_with_failures: 'Completed, with rows to review',
  failed: 'Failed',
}

/** For the history table and its filter, where the full label makes the table scroll. */
export const SHORT_STATUS_LABELS: Record<RunStatus, string> = {
  ...STATUS_LABELS,
  completed_with_failures: 'Rows to review',
}

export const isSettled = (status: RunStatus) => status !== 'queued' && status !== 'working'

export const rowsToLookAt = (counts: RunCounts) => counts.failed + counts.skipped

export const summaryPath = (run: Pick<ImportRun, 'id'>) => `/imports/${run.id}`
